// src/pages/AttentionPage.tsx
//
// ★★★ WAVE 10 — WHAT NEEDS THIS OFFICER BUT IS NOT A QUEUE DECISION.
//
// Two populations, and neither has anywhere else to be:
//
//  1. **Bills a colleague referred to you** (Annex A 37/38). `POST …/refer` writes
//     a row into an append-only ledger. Without this screen that row is a message
//     sent to a mailbox nobody opens — which is not a smaller version of the
//     feature, it is the feature not existing. This repository's first discipline
//     is *"a change is not done when its own half works"*.
//  2. **Bills you paused for information.** A paused bill is deliberately absent
//     from the approval queue: `pendingDecision('INFORMATION_REQUESTED')` is null,
//     because nobody in this office is being waited on. That is right for the
//     queue and it means a paused bill would otherwise be visible ONLY by paging
//     the full bills list — so bills would be paused and quietly forgotten, which
//     is the stranding migration 060 was built to prevent, arriving by a different
//     door.
//
// ══ ★★★ WAVE 11 — THE PAUSED LIST IS NOW A CENSUS, NOT A SAMPLE ══
//
// This header used to read: *"The paused list is filtered from ONE PAGE of
// `GET /api/bills` … on a scope larger than one page it is a sample rather than a
// census — and it says so"*, followed by a note that a status filter on that route
// would fix it and was deferred because *"that route carries a keyset cursor and a
// sort contract"*. It did, and the filter is built now: the route takes
// `?status=`, the cursor carries the filter it was minted under, and — the half
// that matters here — **`total` is narrowed by the filter too**.
//
// So this screen asks the server for paused bills and gets the paused COUNT back.
// `total` is how many bills in this scope are paused, not how many bills exist.
// The old caveat, and the old client-side `.filter()` behind it, are gone.
//
// ⚠ **The caveat is not deleted, it is RESCALED, and it has to be.** The route is
// still page-clamped at `MAX_BILL_LIMIT`, so a scope with more paused bills than
// one page still shows a subset — but the denominator is now the number that
// answers the officer's actual question. "3 of 3 paused" is a census; "3 of 5,125
// bills read" never was one, whatever it said underneath.
//
// ⚠ **And the sort moved to the server with the filter**, because the house rule
// says so: sort server-side when the list is capped or paginated, or a client sort
// reorders one page and lies about the rest. It was defensible while the banner
// stated the denominator in numbers; with the filter it is simply unnecessary.
import { plainProse } from '../lib/plain-words'
import { Link } from 'react-router'
import { IconButton } from '../components/IconButton'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useAuth } from '../state/AuthContext'
import { BillStatusBadge } from '../components/StatusBadge'
import { ErrorCard } from '../components/StateCards'
import { formatCents, formatInstant, formatIsoDate } from '../lib/format'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { useSort, sortParams, type SortState } from '../lib/useSort'
import type { Bill } from '../lib/types'

/**
 * ⚠ The status this screen exists for, named ONCE. Sent to the server as the
 * filter and used nowhere else — there is no client-side `.filter()` left to keep
 * in step with it, which was the point of the change.
 */
const PAUSED_STATUS = 'INFORMATION_REQUESTED'

export function AttentionPage() {
  const { user } = useAuth()
  const inbox = useApi(() => api.referralsToMe(), [])
  // ★★★ WAVE 11 — the sort lives on the PAGE, not in the table, because it is now
  // a query parameter rather than a comparator. Changing it re-queries.
  const { sort, onSort, interacted } = useSort<PausedSortKey>()
  const bills = useApi(
    () => api.bills({ status: [PAUSED_STATUS], ...sortParams(sort) }),
    [sort.column, sort.dir],
  )

  const paused = bills.data?.bills ?? []
  const total = bills.data?.total ?? 0
  // ⚠ The SAME two-part test `ApprovalQueuePage` learned to make: before the
  // response arrives, `total` and `paused.length` are both 0, so
  // `total > paused.length` is false and a completeness claim keyed on it would be
  // made over a page that had read nothing at all.
  //
  // ★★★ AND A THIRD PART NOW, which is the honesty the echo exists for. `total` is
  // only the PAUSED count if the server actually applied the filter. Against a
  // gateway that predates `?status=` the parameter is ignored, `total` is the whole
  // scope, and every number on this screen would be wrong in the direction that
  // reads as reassuring — a full list of paused bills that is nothing of the kind.
  // `appliedStatusFilter` is how a client can tell "narrowed" from "accepted and
  // discarded", so the claim is keyed on it rather than on hope.
  const serverFiltered = (bills.data?.appliedStatusFilter ?? []).includes(PAUSED_STATUS)
  const pausedListComplete = bills.data !== undefined && serverFiltered && total === paused.length

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl">Your attention</h1>
        <p className="text-[var(--sgds-body-color-subtle)] mt-1 max-w-[75ch]">
          Bills that need you but are not waiting for your decision: what colleagues have asked you to look at,
          and what is paused pending information from a provider. Decisions live on the{' '}
          <Link to="/approvals" className="pt-ref-link">
            approval queue
          </Link>
          .
        </p>
      </header>

      {/* ─────────────────────────── referrals ─────────────────────────── */}
      <section className="pt-card">
        <h2 className="text-lg mb-1">Referred to you</h2>
        <p className="text-sm text-[var(--sgds-color-subtle)] mb-4 max-w-[70ch]">
          A colleague asked you to look at these. A referral changes nothing about a bill: it stays in whatever
          queue it was already in, and it is still decided by whoever holds that queue&rsquo;s authority.
        </p>

        {inbox.error !== null ? (
          <ErrorCard title="Could not load your referrals" message={inbox.error} onRetry={inbox.reload} />
        ) : inbox.loading && inbox.data === undefined ? (
          <p className="text-sm text-[var(--sgds-body-color-subtle)]" role="status" aria-busy="true">
            Loading what colleagues have asked you to look at…
          </p>
        ) : (inbox.data?.referrals.length ?? 0) === 0 ? (
          <div className="pt-well p-4">
            <p className="font-semibold">Nobody has referred you a bill</p>
            <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
              {/* ⚠ "Not built" and "no data" are different statements, and this is
                  the second one. The referral feature exists and this list is
                  genuinely empty — said explicitly, because four unbuilt bands
                  that read exactly like four empty ones were filed as bugs on this
                  product once already. */}
              Nothing is waiting for you. When a colleague
              refers you a bill, it appears here with what they asked.
            </p>
          </div>
        ) : (
          <>
            {inbox.data?.truncated === true && (
              <div role="alert" className="pt-well p-4 mb-3">
                <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">This list is incomplete</p>
                <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
                  More bills have been referred to you than this screen will load. Some are not shown.
                </p>
              </div>
            )}
            <ul className="flex flex-col gap-3 m-0 p-0 list-none">
              {inbox.data?.referrals.map((referral) => (
                <li key={referral.billReference} className="pt-well p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <p className="font-semibold">
                      <Link to={`/bills/${encodeURIComponent(referral.billReference)}`} className="pt-ref-link pt-mono">
                        {referral.billReference}
                      </Link>{' '}
                      <span className="pt-tabular font-normal">{formatCents(referral.amount)}</span>{' '}
                      <span className="text-sm font-normal text-[var(--sgds-color-subtle)]">
                        {referral.agencyCode}
                      </span>
                    </p>
                    <p className="text-sm pt-tabular text-[var(--sgds-color-subtle)]">
                      {formatInstant(referral.referredAt)}
                    </p>
                  </div>
                  <p className="text-sm mt-1">
                    <BillStatusBadge status={referral.billStatus} />
                  </p>
                  <p className="text-sm mt-2">
                    {/* ⚠ `null` means the individual is WITHHELD from this role, not
                        unknown — the gateway redacts a referrer with the same gate
                        it redacts a deciding officer, because a referral names a
                        person just as surely as a decision does. */}
                    {referral.referredByDisplayName !== null ? (
                      <>
                        asked by <strong className="font-semibold">{referral.referredByDisplayName}</strong>
                      </>
                    ) : (
                      <span className="text-[var(--sgds-body-color-subtle)]">
                        asked by a colleague (the individual is not disclosed to your role)
                      </span>
                    )}
                  </p>
                  <figure className="pt-well p-3 mt-3 m-0">
                    {/* Their own words. The database refuses a referral with no
                        comment, so this is never empty. */}
                    <blockquote className="text-sm m-0">{referral.comment}</blockquote>
                    <figcaption className="text-xs mt-2 text-[var(--sgds-color-subtle)]">
                      What your colleague asked, as recorded on the bill&rsquo;s permanent history.
                    </figcaption>
                  </figure>
                </li>
              ))}
            </ul>
          </>
        )}

        {inbox.data !== undefined && inbox.data.notes.length > 0 && (
          <ul className="mt-4 flex flex-col gap-1 text-xs text-[var(--sgds-color-subtle)] m-0 p-0 list-none">
            {inbox.data.notes.map((note) => (
              <li key={note}>{plainProse(note)}</li>
            ))}
          </ul>
        )}
      </section>

      {/* ─────────────────────────── paused bills ─────────────────────────── */}
      <section className="pt-card">
        <h2 className="text-lg mb-1">Paused for information</h2>
        <p className="text-sm text-[var(--sgds-color-subtle)] mb-4 max-w-[70ch]">
          These bills are waiting on a healthcare service provider, not on this office. No approval or rejection
          can be recorded against them until they are resumed, which returns each one to the exact queue it was
          paused out of. They are deliberately absent from the approval queue, which is why they are listed here.
        </p>

        {bills.error !== null ? (
          <ErrorCard title="Could not load bills" message={bills.error} onRetry={bills.reload} />
        ) : bills.loading && bills.data === undefined ? (
          <p className="text-sm text-[var(--sgds-body-color-subtle)]" role="status" aria-busy="true">
            Loading paused bills…
          </p>
        ) : paused.length === 0 ? (
          <div className="pt-well p-4">
            <p className="font-semibold">
              {/* ★★★ WAVE 11 — this is now a statement about the WHOLE scope, because
                  the server counted the whole scope. It was "among the bills read so
                  far", which was honest and nearly useless. */}
              {pausedListComplete ? 'No bill in your scope is paused' : 'No paused bill found'}
            </p>
            <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
              {pausedListComplete
                ? 'The server counted every bill in your scope to answer this, not just the ones on this page. Pausing a bill is done from the bill’s own screen.'
                : serverFiltered
                  ? `The server reports ${total} paused bill${total === 1 ? '' : 's'} in your scope but returned none on this page, which should not happen: reload, and report it if it persists.`
                  : 'PRS2.0 could not narrow this list to paused bills by status. Use the Bills list, sorted by Status, instead.'}
            </p>
          </div>
        ) : (
          <>
            {!pausedListComplete && (
              <div role="alert" className="pt-well p-4 mb-3">
                <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">This list may be incomplete</p>
                <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
                  {/* ⚠ Two different incompletenesses, and they need different words.
                      One is a page clamp over a filtered population — an ordinary,
                      countable shortfall. The other is a gateway that ignored the
                      filter, where the denominator is not paused bills at all and no
                      arithmetic on this screen means what it looks like. */}
                  {serverFiltered
                    ? `Showing ${paused.length} of ${total} paused bills in your scope: this page does not hold them all. Sort to bring others to the top, or use the Bills list to page through them.`
                    : `PRS2.0 could not narrow the list by status, so these are the paused bills among the first ${paused.length} of ${total} bills read, not necessarily all of them.`}
                </p>
              </div>
            )}
            <PausedTable bills={paused} sort={sort} onSort={onSort} interacted={interacted} busy={bills.loading} />
          </>
        )}
      </section>

      <div>
        <IconButton glyph="refresh" label="Refresh" onClick={inbox.reload} />
      </div>

      {user === undefined && (
        <p className="text-sm text-[var(--sgds-body-color-subtle)]">Loading your scope…</p>
      )}
    </div>
  )
}

/**
 * ★★ THE PAUSED TABLE — SORTABLE HEADERS ON EVERY COLUMN, like every other table
 * in this product.
 *
 * ══ ★★★ WAVE 11 — IT SORTS ON THE SERVER NOW, AND THE OLD ARGUMENT IS GONE ══
 *
 * This header used to defend a CLIENT sort. The house rule is *sort server-side
 * when the list is capped or paginated, otherwise a client sort reorders one page
 * and silently lies about the rest* — and the defence was that the operative word
 * is SILENTLY: the banner above stated the denominator in numbers, so a reader was
 * told what they were sorting before they sorted it. That was true and it was
 * always the weaker answer.
 *
 * With `?status=` on `GET /api/bills` the list is the paused population and the
 * sort is a query parameter, so the argument does not need making. Every one of
 * these five columns is a member of `BILL_SORT_COLUMNS`, so the server can order
 * by all of them; `sortPaused` and its comparator are deleted rather than left
 * unused, because an unused comparator is the thing the next reader reaches for.
 *
 * ⚠ `busy` is now REAL — a server sort has an in-flight window in which the rows
 * on screen are still in the old order, which the client sort did not. Passed in
 * from the page's own loading state so `SortAnnouncement` says "sorting…" rather
 * than announcing a row count that has not changed yet.
 *
 * ⚠ The server's order is total: `buildBillOrderBy` appends `b.reference ASC`
 * unconditionally, so two renders of one population agree and the table can be
 * read twice. No column here is nullable, so there is no nulls-last question to
 * answer — stated because the next column added might be.
 */
function PausedTable({
  bills,
  sort,
  onSort,
  interacted,
  busy,
}: {
  bills: readonly Bill[]
  sort: SortState<PausedSortKey>
  onSort: (key: PausedSortKey) => void
  interacted: boolean
  busy: boolean
}) {
  const rows = bills

  return (
    <>
      <div className="pt-table-wrap">
        <table className="pt-table">
          <caption className="sr-only">
            Bills in your scope that are paused pending information from a healthcare service provider
          </caption>
          <thead>
            <tr>
              <SortableTh label="Reference" sortKey="reference" sort={sort} onSort={onSort} />
              <SortableTh label="Agency" sortKey="agencyCode" sort={sort} onSort={onSort} />
              <SortableTh label="Amount" sortKey="amount" sort={sort} onSort={onSort} align="right" />
              <SortableTh label="Service date" sortKey="serviceDate" sort={sort} onSort={onSort} />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {rows.map((bill) => (
              <tr key={bill.reference}>
                <td>
                  <Link to={`/bills/${encodeURIComponent(bill.reference)}`} className="pt-ref-link pt-mono">
                    {bill.reference}
                  </Link>
                </td>
                <td>{bill.agencyCode}</td>
                <td className="text-right pt-tabular">{formatCents(bill.amount)}</td>
                {/* ★ WAVE 26 — `dd MMM yyyy` like every other date column; this
                    was the one table showing the raw ISO string (sgds-reconciliation §1a). */}
                <td className="pt-tabular">{formatIsoDate(bill.serviceDate)}</td>
                <td>
                  <BillStatusBadge status={bill.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/*
        ⚠ THE SPOKEN HALF OF A SORT. `aria-sort` on the header tells a screen reader
        the state when the user is ON that header; it says nothing at the moment the
        order changes, which a sighted user sees happen.

        ★★★ `busy` is the page's real loading state since Wave 11, not `false`. This
        sort re-queries, so there IS an in-flight window in which the rows on screen
        are still in the old order — announcing a settled row count during it would
        tell a screen-reader user the sort had finished when it had not.
      */}
      <SortAnnouncement
        sort={sort}
        interacted={interacted}
        busy={busy}
        columnLabels={PAUSED_COLUMN_LABELS}
        rowCount={rows.length}
      />
    </>
  )
}

type PausedSortKey = 'reference' | 'agencyCode' | 'amount' | 'serviceDate' | 'status'

const PAUSED_COLUMN_LABELS: Readonly<Record<PausedSortKey, string>> = {
  reference: 'Reference',
  agencyCode: 'Agency',
  amount: 'Amount',
  serviceDate: 'Service date',
  status: 'Status',
}

// ★★★ WAVE 11 — `sortPaused` IS DELETED, not left unused.
//
// It was a client-side comparator over a client-side filter of one page. Both
// halves are gone: `?status=` narrows on the server and `?sort=`/`?dir=` order
// there, so the rows arrive in the order the table displays.
//
// ⚠ Deleted rather than kept "in case", because an unused comparator beside a
// server-sorted table is exactly what the next reader reaches for when they add a
// sixth column — and it would silently reorder one page again. Its two properties
// are not lost: the server's `buildBillOrderBy` appends `b.reference ASC`
// unconditionally, which is the total order the tiebreak provided, and it is
// asserted in `bill-repository.test.ts` rather than here.
